import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToMenu = () => {
    const element = document.querySelector('#carta');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/hero-portada.jpg"
          alt="Plato de ceviche peruano"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-coffee/60 via-coffee/40 to-coffee/70" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-gold/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-48 h-48 bg-terracotta/20 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-6"
        >
          <span className="font-accent text-gold text-2xl md:text-3xl">
            Bienvenidos a
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 tracking-tight text-shadow"
          data-testid="hero-title"
        >
          SALKANTAY
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-white/90 text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto mb-4 leading-relaxed"
          data-testid="hero-subtitle"
        >
          La auténtica gastronomía peruana en Madrid
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-gold font-medium text-base md:text-lg max-w-2xl mx-auto mb-10"
        >
          Calidad, sabor y tradición en cada bocado
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
        >
          <Button
            asChild
            size="lg"
            className="bg-terracotta hover:bg-terracotta-dark text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-300 hover:scale-105 shadow-xl"
            data-testid="hero-whatsapp-btn"
          >
            <a
              href="https://wa.me/34623513773?text=Hola,%20me%20gustaría%20hacer%20una%20reserva"
              target="_blank"
              rel="noopener noreferrer"
            >
              Reserva tu Mesa
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
          <Button
            onClick={scrollToMenu}
            size="lg"
            variant="outline"
            className="bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
            data-testid="hero-menu-btn"
          >
            Ver Nuestra Carta
          </Button>
        </motion.div>

        {/* Quick Info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col md:flex-row gap-6 md:gap-12 justify-center items-center text-white/80"
        >
          <a
            href="https://maps.google.com/?q=Calle+Sierra+Contraviesa+1+Madrid"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:text-white transition-colors"
            data-testid="hero-location"
          >
            <MapPin className="w-5 h-5 text-gold" />
            <span>C/ Sierra Contraviesa 1, Madrid</span>
          </a>
          <a
            href="tel:+34623513773"
            className="flex items-center gap-2 hover:text-white transition-colors"
            data-testid="hero-phone"
          >
            <Phone className="w-5 h-5 text-gold" />
            <span>+34 623 51 37 73</span>
          </a>
        </motion.div>
      </div>

      {/* Wave Separator */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full"
          preserveAspectRatio="none"
        >
          <path
            d="M0 120L48 110C96 100 192 80 288 70C384 60 480 60 576 65C672 70 768 80 864 85C960 90 1056 90 1152 85C1248 80 1344 70 1392 65L1440 60V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0Z"
            fill="#FDFBF7"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
